import type { VehicleDetails } from "@/types/vehicleDetails";
import { normalizeVehicleType } from "@/utils/vehicleType";

/**
 * Shared step-1 completion logic for the dealer post-an-ad and valuation flows.
 *
 * These two gates were hand-copied, and they drifted: the valuation copy demanded
 * `bodyType` unconditionally while the post-an-ad copy already exempted motorcycles, so
 * the valuation progress pill could never unlock for an MC (MC mode clears bodyType by
 * design - see useVehicleForm's newVehicleType === "mc" reset). Per the standing rule
 * that flows must never diverge, both widgets now call this one function.
 *
 * Only bodyType is exempted for MCs. Engine and horsepower stay required: the MC flow
 * populates both (its engine picker offers cc-with-layout chips such as
 * "500cc V-Twin", and the horsepower input renders for MCs), which the stored MC lead
 * data confirms.
 */

/** The three spellings of "electric" that reach this form's fuelType field. */
export const isElectricVehicle = (
  details: Pick<VehicleDetails, "fuelType">
): boolean =>
  details.fuelType === "El" ||
  details.fuelType === "Elbil" ||
  details.fuelType === "ELECTRIC";

/** Engine is required for everything except EVs, which are auto-set to "Electric". */
export const hasRequiredEngine = (
  details: Pick<VehicleDetails, "fuelType" | "engine">
): boolean => (isElectricVehicle(details) ? true : !!details.engine);

/** Electric drive range is required only for EVs. */
export const hasRequiredElectricDriveRange = (
  details: Pick<VehicleDetails, "fuelType" | "electricDriveRange">
): boolean =>
  isElectricVehicle(details)
    ? !!(details.electricDriveRange && details.electricDriveRange > 0)
    : true;

/** Body type is required for everything except motorcycles, which have none. */
export const requiresBodyType = (
  details: Pick<VehicleDetails, "vehicleType">
): boolean => normalizeVehicleType(details.vehicleType) !== "mc";

/**
 * The narrower "Bildetaljer" section check used by the post-an-ad flow to decide whether
 * the dealer may advance past that section. Existed as two identical inline copies in
 * CarPostAnAdWidget (the model-change handler and goToStep); extracted so the MC rule
 * lives in one place alongside the step-1 gate.
 */
export const isBildetaljerComplete = (
  details: Partial<VehicleDetails>
): boolean => {
  const d = details as VehicleDetails;
  return !!(
    d.fuelType &&
    hasRequiredEngine(d) &&
    (requiresBodyType(d) ? !!d.bodyType : true) &&
    d.transmission &&
    d.color
  );
};

export interface VehicleInfoCompletionOptions {
  /**
   * The valuation flow collects condition inside step 1; the post-an-ad flow collects it
   * later in its own test step. This is the ONLY difference between the two gates.
   */
  requireCondition?: boolean;
}

/**
 * True when every step-1 vehicle-information field the flow requires is filled in.
 */
export const isVehicleInfoComplete = (
  details: Partial<VehicleDetails>,
  options: VehicleInfoCompletionOptions = {}
): boolean => {
  const d = details as VehicleDetails;

  return !!(
    d.make &&
    d.model &&
    d.year &&
    d.fuelType &&
    hasRequiredEngine(d) &&
    (requiresBodyType(d) ? !!d.bodyType : true) &&
    d.transmission &&
    d.color &&
    d.equipment &&
    d.equipment.length > 0 &&
    (options.requireCondition ? !!d.condition : true) &&
    d.mileage &&
    d.keys &&
    d.horsepower &&
    hasRequiredElectricDriveRange(d)
  );
};
